import { hostedAssistantsCreate, hostedAssistantsList, hostedAssistantsUpdate } from '@owlbert/core'
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Card,
  Center,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { IconArrowLeft, IconBooks, IconCheck, IconPlus, IconRobot } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import React from 'react'
import {
  createAssistant,
  fetchAssistants,
  updateAssistant,
  type FetchAssistantsResponse,
  type HostedAssistantRow,
} from '../api'
import type { AssistantProposalFields } from '../assistant/assistantProposalFromDescription'
import { useOwlbertWebAuth } from '../auth/OwlbertWebAuthContext'
import {
  WEB_DEFAULT_ASSISTANT_INSTRUCTIONS,
  WEB_DEFAULT_OWLBERT_ASSISTANT_ID,
  withDefaultOwlbertAssistant,
} from '../webAssistantDefaults'
import { AssistantAutoCreatePanel } from './AssistantAutoCreatePanel'
import { AssistantCatalogPickerModal } from './AssistantCatalogPickerModal'
import { AssistantModelField } from './AssistantModelField'

export function parseAssistantsToolsCsv(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

export type HostedAssistantManagerProps = {
  /** When true, rows highlight `selectedAssistantId` and click selects (Chat sidebar). */
  selectable?: boolean
  selectedAssistantId?: string | null
  onSelectAssistant?: (id: string) => void
  /** `settings`: avatar card grid with inline edit; `sidebar`: compact selectable list. */
  layout?: 'sidebar' | 'settings'
}

type SettingsView = 'list' | 'edit'

const CARD_SIZE = 160

/**
 * Lists hosted/BFF assistants.
 * - `settings` layout: avatar card grid; clicking a card replaces the list with an inline edit form.
 * - `sidebar` layout: compact selectable list with a modal for create/edit.
 */
export const HostedAssistantManager: React.FC<HostedAssistantManagerProps> = ({
  selectable = false,
  selectedAssistantId = null,
  onSelectAssistant,
  layout = 'sidebar',
}) => {
  const auth = useOwlbertWebAuth()
  const browserReady = auth.configured && auth.sessionReady && Boolean(auth.workspaceId) && Boolean(auth.bundle?.client)
  const catalogReady = auth.sessionReady && Boolean(auth.bundle?.client)

  const [assistants, setAssistants] = React.useState<HostedAssistantRow[]>([])
  const [assistantsMeta, setAssistantsMeta] = React.useState<Pick<FetchAssistantsResponse, 'storage' | 'message' | 'error'>>({
    storage: 'none',
  })

  // ── Settings-layout view state ────────────────────────────────────────────
  const [settingsView, setSettingsView] = React.useState<SettingsView>('list')
  const [editingAssistant, setEditingAssistant] = React.useState<HostedAssistantRow | null>(null)

  // ── Sidebar-layout modal state ────────────────────────────────────────────
  const [assistModalOpen, setAssistModalOpen] = React.useState(false)
  const [assistModalMode, setAssistModalMode] = React.useState<'create' | 'edit'>('create')
  const [assistEditingId, setAssistEditingId] = React.useState<string | null>(null)

  // Shared form fields (used by both layouts)
  const [assistName, setAssistName] = React.useState('')
  const [assistDescription, setAssistDescription] = React.useState('')
  const [assistInstructions, setAssistInstructions] = React.useState('')
  const [assistGreeting, setAssistGreeting] = React.useState('')
  const [assistLlm, setAssistLlm] = React.useState('')
  const [assistTools, setAssistTools] = React.useState('')
  const [assistSaving, setAssistSaving] = React.useState(false)
  const [assistWizardKey, setAssistWizardKey] = React.useState(0)
  const [assistPendingProposal, setAssistPendingProposal] = React.useState<AssistantProposalFields | null>(null)
  const [assistCreateManual, setAssistCreateManual] = React.useState(false)
  const [catalogPickerOpen, setCatalogPickerOpen] = React.useState(false)

  const hostedListOk = assistantsMeta.storage === 'hosted' && !assistantsMeta.error
  const canManageAssistants = (browserReady && Boolean(auth.workspaceId)) || hostedListOk || catalogReady

  // ── Data loading ──────────────────────────────────────────────────────────
  const reloadAssistantsBff = React.useCallback(async () => {
    try {
      const r = await fetchAssistants()
      setAssistants(withDefaultOwlbertAssistant(r.assistants as HostedAssistantRow[]))
      setAssistantsMeta({ storage: r.storage, message: r.message, error: r.error })
    } catch {
      setAssistants(withDefaultOwlbertAssistant([]))
      setAssistantsMeta({ storage: 'none', message: 'Could not reach /api/assistants.' })
    }
  }, [])

  React.useEffect(() => {
    if (browserReady) return
    void reloadAssistantsBff()
  }, [browserReady, reloadAssistantsBff])

  const loadAssistantsBrowser = React.useCallback(async () => {
    if (!browserReady || !auth.bundle || !auth.workspaceId) return
    const rows = await hostedAssistantsList(auth.bundle.client.owlbertHosted.assistants, { workspaceId: auth.workspaceId })
    setAssistants(withDefaultOwlbertAssistant(rows as unknown as HostedAssistantRow[]))
    setAssistantsMeta({ storage: 'hosted' })
  }, [auth.bundle, auth.workspaceId, browserReady])

  React.useEffect(() => {
    if (!browserReady) return
    void loadAssistantsBrowser().catch((e) =>
      notifications.show({ color: 'red', title: 'Assistants', message: String(e) }),
    )
  }, [browserReady, loadAssistantsBrowser])

  // ── Helpers: populate form fields from a row ──────────────────────────────
  const populateForm = (a: HostedAssistantRow | null) => {
    setAssistName(a?.name ?? '')
    setAssistDescription(a?.description ?? '')
    setAssistInstructions(a?.instructions ?? WEB_DEFAULT_ASSISTANT_INSTRUCTIONS)
    setAssistGreeting(a?.greeting ?? 'Hello! How can I help?')
    setAssistLlm(a?.llm ?? '')
    setAssistTools((a?.tools ?? []).join(', '))
    setAssistPendingProposal(null)
    setAssistCreateManual(false)
  }

  // ── Settings layout: open inline edit ────────────────────────────────────
  const openInlineEdit = (a: HostedAssistantRow) => {
    setEditingAssistant(a)
    populateForm(a)
    setSettingsView('edit')
  }

  const openInlineCreate = () => {
    setEditingAssistant(null)
    populateForm(null)
    setAssistWizardKey((k) => k + 1)
    setSettingsView('edit')
  }

  const closeInlineEdit = () => {
    setSettingsView('list')
    setEditingAssistant(null)
    setAssistSaving(false)
    setAssistPendingProposal(null)
    setAssistCreateManual(false)
    setCatalogPickerOpen(false)
  }

  // ── Sidebar layout: modal helpers ─────────────────────────────────────────
  const openCreateAssistantModal = () => {
    setAssistModalMode('create')
    setAssistEditingId(null)
    populateForm(null)
    setAssistWizardKey((k) => k + 1)
    setAssistModalOpen(true)
  }

  const openEditAssistantModal = (a: HostedAssistantRow) => {
    setAssistModalMode('edit')
    setAssistEditingId(a.id)
    populateForm(a)
    setAssistModalOpen(true)
  }

  const closeAssistantModal = () => {
    setAssistModalOpen(false)
    setAssistEditingId(null)
    setAssistSaving(false)
    setAssistPendingProposal(null)
    setAssistCreateManual(false)
    setCatalogPickerOpen(false)
  }

  // ── Save logic (shared) ────────────────────────────────────────────────────
  const onSaveAssistant = async (opts: { mode: 'create' | 'edit'; editingId?: string | null; onSuccess: () => void }) => {
    if (!assistName.trim()) {
      notifications.show({ color: 'yellow', message: 'Assistant name is required.' })
      return
    }
    if (!assistLlm.trim()) {
      notifications.show({ color: 'yellow', message: 'Pick a model.' })
      return
    }
    if (!canManageAssistants) {
      notifications.show({ color: 'yellow', message: 'Hosted assistants are not available in this mode.' })
      return
    }

    const tools = parseAssistantsToolsCsv(assistTools)
    const payload = {
      name: assistName.trim(),
      description: assistDescription,
      instructions: assistInstructions,
      greeting: assistGreeting,
      llm: assistLlm.trim(),
      tools,
    }

    setAssistSaving(true)
    try {
      if (browserReady && auth.bundle && auth.workspaceId) {
        const uds = auth.bundle.client.owlbertHosted.assistants
        const wid = auth.workspaceId
        if (opts.mode === 'create') {
          await hostedAssistantsCreate(uds, {
            workspaceId: wid,
            input: {
              workspace_id: null,
              name: payload.name,
              description: payload.description,
              instructions: payload.instructions,
              greeting: payload.greeting,
              llm: payload.llm,
              tools,
              memory: false,
              use_memory: null,
              use_history: null,
              chat_history_config: null,
              use_custom_llm: null,
              mcp_sources: [],
            },
          })
          notifications.show({ color: 'green', message: 'Assistant created.' })
          await loadAssistantsBrowser()
        } else if (opts.editingId) {
          await hostedAssistantsUpdate(uds, {
            workspaceId: wid,
            input: {
              id: opts.editingId,
              name: payload.name,
              description: payload.description,
              instructions: payload.instructions,
              greeting: payload.greeting,
              llm: payload.llm,
              tools,
            },
          })
          notifications.show({ color: 'green', message: 'Assistant updated.' })
          await loadAssistantsBrowser()
        }
        opts.onSuccess()
        return
      }

      if (assistantsMeta.storage === 'hosted') {
        if (opts.mode === 'create') {
          await createAssistant(payload)
          notifications.show({ color: 'green', message: 'Assistant created.' })
          await reloadAssistantsBff()
        } else if (opts.editingId) {
          await updateAssistant(opts.editingId, payload)
          notifications.show({ color: 'green', message: 'Assistant updated.' })
          await reloadAssistantsBff()
        }
        opts.onSuccess()
        return
      }

      notifications.show({ color: 'yellow', message: 'Cannot save assistants in this mode.' })
    } catch (e) {
      notifications.show({ color: 'red', title: 'Save failed', message: String(e) })
    } finally {
      setAssistSaving(false)
    }
  }

  const onCreateAssistantFromWizard = async (onSuccess: () => void) => {
    if (!assistPendingProposal) {
      notifications.show({ color: 'yellow', message: 'Generate a proposal first.' })
      return
    }
    if (!canManageAssistants) {
      notifications.show({ color: 'yellow', message: 'Hosted assistants are not available in this mode.' })
      return
    }
    if (!assistLlm.trim()) {
      notifications.show({ color: 'yellow', message: 'Pick a model.' })
      return
    }
    const p = assistPendingProposal
    const tools: string[] = []
    const greeting = p.greeting?.trim() || 'Hello! How can I help?'
    const llm = assistLlm.trim()

    setAssistSaving(true)
    try {
      if (browserReady && auth.bundle && auth.workspaceId) {
        const uds = auth.bundle.client.owlbertHosted.assistants
        const wid = auth.workspaceId
        await hostedAssistantsCreate(uds, {
          workspaceId: wid,
          input: {
            workspace_id: null,
            name: p.name.trim(),
            description: p.description,
            instructions: p.instructions,
            greeting,
            llm,
            tools,
            memory: false,
            use_memory: null,
            use_history: null,
            chat_history_config: null,
            use_custom_llm: null,
            mcp_sources: [],
          },
        })
        notifications.show({ color: 'green', message: 'Assistant created.' })
        await loadAssistantsBrowser()
        onSuccess()
        return
      }

      if (assistantsMeta.storage === 'hosted') {
        await createAssistant({
          name: p.name.trim(),
          description: p.description,
          instructions: p.instructions,
          greeting,
          llm,
          tools,
        })
        notifications.show({ color: 'green', message: 'Assistant created.' })
        await reloadAssistantsBff()
        onSuccess()
        return
      }

      notifications.show({ color: 'yellow', message: 'Cannot save assistants in this mode.' })
    } catch (e) {
      notifications.show({ color: 'red', title: 'Create failed', message: String(e) })
    } finally {
      setAssistSaving(false)
    }
  }

  // ── Shared form fields JSX ─────────────────────────────────────────────────
  const assistantFormFields = (disabled = false) => (
    <Stack gap="md">
      <TextInput
        size="md"
        label="Name"
        required
        disabled={disabled}
        value={assistName}
        onChange={(e) => setAssistName(e.currentTarget.value)}
      />
      <AssistantModelField
        value={assistLlm}
        onChange={setAssistLlm}
        catalogReady={catalogReady}
        bundle={auth.bundle}
        disabled={disabled}
      />
      <Textarea
        size="md"
        label="Description"
        autosize
        minRows={3}
        maxRows={6}
        disabled={disabled}
        value={assistDescription}
        onChange={(e) => setAssistDescription(e.currentTarget.value)}
      />
      <Textarea
        size="md"
        label="Instructions"
        description="System prompt for this assistant."
        autosize
        minRows={8}
        maxRows={20}
        disabled={disabled}
        value={assistInstructions}
        onChange={(e) => setAssistInstructions(e.currentTarget.value)}
      />
      <Textarea
        size="md"
        label="Greeting"
        autosize
        minRows={3}
        maxRows={6}
        disabled={disabled}
        value={assistGreeting}
        onChange={(e) => setAssistGreeting(e.currentTarget.value)}
      />
      <TextInput
        size="md"
        label="Tools"
        description="Optional. Comma-separated tool ids; leave blank for defaults."
        disabled={disabled}
        value={assistTools}
        onChange={(e) => setAssistTools(e.currentTarget.value)}
        placeholder="Leave blank for defaults"
      />
    </Stack>
  )

  // ── AI wizard section (used in create flow) ────────────────────────────────
  const wizardSection = (onSuccess: () => void) => (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        Describe what you want, pick a model assigned to your account, then generate a proposal or start from a template.
      </Text>
      <AssistantModelField
        value={assistLlm}
        onChange={setAssistLlm}
        catalogReady={catalogReady}
        bundle={auth.bundle}
        disabled={assistSaving}
      />
      <AssistantAutoCreatePanel
        key={assistWizardKey}
        browserReady={browserReady}
        bundle={auth.bundle}
        disabled={!canManageAssistants || assistSaving}
        onProposal={(p) => setAssistPendingProposal(p)}
      />
      {assistPendingProposal ? (
        <Paper withBorder p="sm" radius="md">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Preview</Text>
          <Text fw={600} mt={6}>{assistPendingProposal.name}</Text>
          {assistPendingProposal.description ? (
            <Text size="sm" c="dimmed" mt={4}>{assistPendingProposal.description}</Text>
          ) : null}
          <Text size="xs" c="dimmed" mt="sm" lineClamp={5} style={{ whiteSpace: 'pre-wrap' }}>
            {assistPendingProposal.instructions}
          </Text>
          <Button
            fullWidth
            mt="md"
            className="accent-button"
            loading={assistSaving}
            onClick={() => void onCreateAssistantFromWizard(onSuccess)}
          >
            Create assistant
          </Button>
        </Paper>
      ) : null}
      <Group justify="space-between" align="center" wrap="wrap" gap="sm" mt="xs">
        <Group gap="xs" wrap="wrap">
          <Button
            variant="light"
            size="sm"
            leftSection={<IconBooks size={16} />}
            disabled={!auth.bundle?.client || !canManageAssistants}
            onClick={() => setCatalogPickerOpen(true)}
          >
            Pick from catalog
          </Button>
          <Button variant="subtle" size="sm" onClick={() => setAssistCreateManual(true)}>
            Blank assistant — full form
          </Button>
        </Group>
      </Group>
    </Stack>
  )

  // ════════════════════════════════════════════════════════════════════════════
  // SETTINGS LAYOUT — avatar card grid + inline edit
  // ════════════════════════════════════════════════════════════════════════════
  if (layout === 'settings') {
    const isCreating = settingsView === 'edit' && editingAssistant === null
    const isEditing = settingsView === 'edit' && editingAssistant !== null
    const isReadOnly = isEditing && editingAssistant?.id === WEB_DEFAULT_OWLBERT_ASSISTANT_ID

    if (settingsView === 'edit') {
      return (
        <Stack gap="md">
          {/* Breadcrumb / back nav */}
          <Group gap="xs">
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconArrowLeft size={14} />}
              onClick={closeInlineEdit}
            >
              All assistants
            </Button>
            {editingAssistant && (
              <>
                <Text c="dimmed" size="sm">/</Text>
                <Text size="sm" fw={500}>{editingAssistant.name}</Text>
              </>
            )}
            {isCreating && (
              <>
                <Text c="dimmed" size="sm">/</Text>
                <Text size="sm" fw={500}>New assistant</Text>
              </>
            )}
          </Group>

          {/* Form body */}
          {isCreating && !assistCreateManual ? (
            wizardSection(closeInlineEdit)
          ) : (
            <Stack gap="sm">
              {isReadOnly ? (
                <Paper withBorder p="sm" radius="md">
                  <Text size="sm" fw={600}>Built-in assistant (read-only)</Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    Owlbert is the default assistant available to everyone and can’t be edited. Go back and create a
                    new assistant to make your own.
                  </Text>
                </Paper>
              ) : null}
              {isCreating && (
                <Button variant="subtle" size="sm" w="fit-content" onClick={() => setAssistCreateManual(false)}>
                  Back to AI setup
                </Button>
              )}
              {assistantFormFields(isReadOnly)}
            </Stack>
          )}

          {/* Save / Cancel */}
          {((isEditing && !isReadOnly) || assistCreateManual) && (
            <Group justify="flex-end" gap="sm" pt="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
              <Button variant="subtle" onClick={closeInlineEdit}>Cancel</Button>
              <Button
                leftSection={<IconCheck size={16} />}
                color="teal"
                loading={assistSaving}
                onClick={() =>
                  void onSaveAssistant({
                    mode: isCreating ? 'create' : 'edit',
                    editingId: editingAssistant?.id,
                    onSuccess: closeInlineEdit,
                  })
                }
              >
                Save
              </Button>
            </Group>
          )}

          <AssistantCatalogPickerModal
            opened={catalogPickerOpen}
            onClose={() => setCatalogPickerOpen(false)}
            trpcClient={auth.bundle?.client ?? null}
            apiBase={auth.backendUrl}
            onPick={(p) => {
              setAssistPendingProposal(p)
              notifications.show({ color: 'teal', message: 'Template applied — review the preview, then create.' })
            }}
          />
        </Stack>
      )
    }

    // List view — avatar cards
    const sortedAssistants = [...assistants].sort((a, b) => a.name.localeCompare(b.name))
    return (
      <Stack gap="md">
        {assistantsMeta.storage === 'hosted' && assistantsMeta.message ? (
          <Paper withBorder p="sm" radius="md">
            <Text size="xs" c="red">
              {assistantsMeta.message}
              {assistantsMeta.error ? ` (${assistantsMeta.error})` : ''}
            </Text>
          </Paper>
        ) : null}

        {sortedAssistants.length === 0 ? (
          <Center py={48}>
            <Stack gap="md" align="center" maw={360}>
              <Avatar size={72} radius="xl" color="gray"><IconRobot size={36} /></Avatar>
              <Text size="md" c="dimmed" ta="center">
                No assistants yet. Create one to get started.
              </Text>
              <Button
                leftSection={<IconPlus size={16} />}
                disabled={!canManageAssistants}
                onClick={openInlineCreate}
              >
                Create assistant
              </Button>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="lg">
            {sortedAssistants.map((a) => (
              <Card
                key={a.id}
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                miw={CARD_SIZE}
                mih={CARD_SIZE}
                style={{ cursor: 'pointer' }}
                onClick={() => openInlineEdit(a)}
              >
                <Card.Section inheritPadding py="md">
                  <Center>
                    <Avatar size={56} radius="xl" color="blue">
                      <IconRobot size={28} />
                    </Avatar>
                  </Center>
                </Card.Section>
                <Stack gap={4} align="center">
                  <Text fw={600} size="sm" lineClamp={1} ta="center">
                    {a.name || 'Unnamed'}
                  </Text>
                  {a.id === WEB_DEFAULT_OWLBERT_ASSISTANT_ID ? (
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Built-in</Text>
                  ) : null}
                  {a.description && (
                    <Text size="xs" c="dimmed" lineClamp={2} ta="center">
                      {a.description}
                    </Text>
                  )}
                </Stack>
              </Card>
            ))}
            <Card
              key="add"
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              miw={CARD_SIZE}
              mih={CARD_SIZE}
              style={{ cursor: canManageAssistants ? 'pointer' : 'not-allowed', opacity: canManageAssistants ? 1 : 0.6 }}
              onClick={canManageAssistants ? openInlineCreate : undefined}
            >
              <Card.Section inheritPadding py="md">
                <Center h={72}>
                  <Avatar size={56} radius="xl" color="gray">
                    <IconPlus size={28} />
                  </Avatar>
                </Center>
              </Card.Section>
              <Stack gap={4} align="center">
                <Text fw={600} size="sm" c="dimmed">Add assistant</Text>
              </Stack>
            </Card>
          </SimpleGrid>
        )}
      </Stack>
    )
  }

  // ════════════════════════════════════════════════════════════════════════════
  // SIDEBAR LAYOUT — compact selectable list + modal (unchanged)
  // ════════════════════════════════════════════════════════════════════════════
  const listBlock = (
    <Stack gap={6}>
      <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Assistants</Text>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          disabled={!canManageAssistants}
          onClick={(e) => {
            e.stopPropagation()
            openCreateAssistantModal()
          }}
        >
          New
        </Button>
      </Group>

      {assistants.length === 0 ? (
        <Paper withBorder p="sm" radius="md">
          <Text size="sm" fw={600}>No assistants yet</Text>
          <Text size="xs" c="dimmed" mt={4}>Tap <strong>New</strong> to create one.</Text>
        </Paper>
      ) : (
        assistants.map((a) => (
          <Paper
            key={a.id}
            withBorder
            p="sm"
            radius="md"
            style={{
              cursor: selectable ? 'pointer' : 'default',
              outline:
                selectable && selectedAssistantId === a.id ? '2px solid var(--mantine-color-blue-filled)' : undefined,
            }}
            onClick={() => {
              if (selectable) onSelectAssistant?.(a.id)
            }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
              <Box style={{ minWidth: 0 }}>
                <Text size="sm" fw={600}>{a.name}</Text>
                <Text size="xs" c="dimmed" lineClamp={2}>{a.description || a.greeting || '—'}</Text>
              </Box>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label={`Edit ${a.name}`}
                disabled={!canManageAssistants}
                onClick={(e) => {
                  e.stopPropagation()
                  openEditAssistantModal(a)
                }}
              >
                {/* keep edit icon for sidebar compact mode */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </ActionIcon>
            </Group>
          </Paper>
        ))
      )}
    </Stack>
  )

  const modal = (
    <Modal
      opened={assistModalOpen}
      onClose={closeAssistantModal}
      title={assistModalMode === 'create' ? 'New assistant' : 'Edit assistant'}
      size="lg"
    >
      {assistModalMode === 'create' && !assistCreateManual ? (
        <Stack gap="sm">
          {wizardSection(closeAssistantModal)}
          <Group justify="flex-end">
            <Button variant="default" onClick={closeAssistantModal}>Cancel</Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          {assistModalMode === 'create' && assistCreateManual ? (
            <Button variant="subtle" size="sm" w="fit-content" onClick={() => setAssistCreateManual(false)}>
              Back to AI setup
            </Button>
          ) : null}
          {assistModalMode === 'edit' && assistEditingId === WEB_DEFAULT_OWLBERT_ASSISTANT_ID ? (
            <Paper withBorder p="sm" radius="md">
              <Text size="sm" fw={600}>Built-in assistant (read-only)</Text>
              <Text size="xs" c="dimmed" mt={4}>
                Owlbert is the default assistant available to everyone and can’t be edited.
              </Text>
            </Paper>
          ) : null}
          {assistantFormFields(assistModalMode === 'edit' && assistEditingId === WEB_DEFAULT_OWLBERT_ASSISTANT_ID)}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeAssistantModal}>
              {assistModalMode === 'edit' && assistEditingId === WEB_DEFAULT_OWLBERT_ASSISTANT_ID ? 'Close' : 'Cancel'}
            </Button>
            {assistModalMode === 'edit' && assistEditingId === WEB_DEFAULT_OWLBERT_ASSISTANT_ID ? null : (
              <Button
                className="accent-button"
                loading={assistSaving}
                onClick={() =>
                  void onSaveAssistant({
                    mode: assistModalMode,
                    editingId: assistEditingId,
                    onSuccess: closeAssistantModal,
                  })
                }
              >
                Save
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  )

  return (
    <>
      {listBlock}
      {modal}
      <AssistantCatalogPickerModal
        opened={catalogPickerOpen}
        onClose={() => setCatalogPickerOpen(false)}
        trpcClient={auth.bundle?.client ?? null}
        apiBase={auth.backendUrl}
        onPick={(p) => {
          setAssistPendingProposal(p)
          notifications.show({ color: 'teal', message: 'Template applied — review the preview, then create.' })
        }}
      />
    </>
  )
}
